import React, { useState } from "react";
import {
    Box, Container, Typography, TextField, Rating, Button, Alert,
    ToggleButtonGroup, ToggleButton, CircularProgress
} from "@mui/material";
import { Smartphone, TabletMac, Headphones, CheckCircle, RateReview } from "@mui/icons-material";
import { useSearchParams } from "react-router-dom";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import AppBarSection from "./components/main/AppBarSection";

const ReviewWritePage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const applicationId = searchParams.get("id") || "";

    const [deviceType, setDeviceType] = useState<string>("phone");
    const [device, setDevice] = useState("");
    const [rating, setRating] = useState<number | null>(5);
    const [text, setText] = useState("");
    const [name, setName] = useState("");
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const handleSubmit = async () => {
        if (!device.trim() || !text.trim() || !name.trim() || !rating) {
            setError("모든 항목을 입력해주세요.");
            return;
        }
        if (text.trim().length < 20) {
            setError("리뷰를 20자 이상 작성해주세요.");
            return;
        }

        setLoading(true);
        setError("");
        try {
            await addDoc(collection(db, "reviews"), {
                name: name.trim(),
                device: device.trim(),
                deviceType,
                rating,
                text: text.trim(),
                applicationId,
                approved: false,
                createdAt: serverTimestamp(),
            });
            setSubmitted(true);
        } catch (e) {
            setError("등록 중 오류가 발생했습니다. 다시 시도해주세요.");
        } finally {
            setLoading(false);
        }
    };

    if (submitted) {
        return (
            <Box sx={{ background: '#fff', minHeight: '100vh' }}>
                <AppBarSection />
                <Container maxWidth="sm" sx={{ pt: { xs: 12, md: 16 }, pb: 8, textAlign: 'center' }}>
                    <Box sx={{
                        width: 80, height: 80, mx: 'auto', mb: 3,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #dcfce7, #bbf7d0)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <CheckCircle sx={{ fontSize: 40, color: '#16a34a' }} />
                    </Box>
                    <Typography fontWeight={700} sx={{ fontSize: { xs: '1.3rem', md: '1.6rem' }, color: '#0f172a', mb: 1.5 }}>
                        리뷰가 등록되었습니다!
                    </Typography>
                    <Typography sx={{ fontSize: '0.9rem', color: '#64748b', lineHeight: 1.7 }}>
                        소중한 후기 감사합니다.<br />
                        관리자 검토 후 메인 페이지에 게시됩니다.
                    </Typography>
                </Container>
            </Box>
        );
    }

    return (
        <Box sx={{ background: '#f8fafc', minHeight: '100vh' }}>
            <AppBarSection />

            <Container maxWidth="sm" sx={{ pt: { xs: 10, md: 14 }, pb: 8 }}>
                {/* 헤더 */}
                <Box sx={{ textAlign: 'center', mb: 5 }}>
                    <Box sx={{
                        width: 64, height: 64, mx: 'auto', mb: 2.5,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #dbeafe, #bfdbfe)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <RateReview sx={{ fontSize: 30, color: '#2563eb' }} />
                    </Box>
                    <Typography fontWeight={800} sx={{ fontSize: { xs: '1.4rem', md: '1.7rem' }, color: '#0f172a', mb: 1 }}>
                        거래 후기 작성
                    </Typography>
                    <Typography sx={{ fontSize: '0.9rem', color: '#64748b' }}>
                        다원트레이드와의 거래 경험을 공유해주세요
                    </Typography>
                </Box>

                {/* 폼 */}
                <Box sx={{
                    background: '#fff',
                    borderRadius: 3,
                    border: '1px solid #f0f0f0',
                    p: { xs: 3, md: 4 },
                }}>
                    {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

                    {/* 이름 */}
                    <Typography fontWeight={600} sx={{ fontSize: '0.88rem', color: '#0f172a', mb: 1 }}>
                        이름 (닉네임)
                    </Typography>
                    <TextField
                        fullWidth
                        placeholder="홍길동"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        size="small"
                    />

                    {/* 기기 유형 */}
                    <Typography fontWeight={600} sx={{ fontSize: '0.88rem', color: '#0f172a', mb: 1 }}>
                        기기 유형
                    </Typography>
                    <ToggleButtonGroup
                        value={deviceType}
                        exclusive
                        onChange={(_, v) => v && setDeviceType(v)}
                        sx={{ mb: 3, display: 'flex', gap: 1 }}
                    >
                        {[
                            { value: 'phone', label: '스마트폰', icon: <Smartphone sx={{ fontSize: 18 }} /> },
                            { value: 'tablet', label: '태블릿', icon: <TabletMac sx={{ fontSize: 18 }} /> },
                            { value: 'airpods', label: '에어팟', icon: <Headphones sx={{ fontSize: 18 }} /> },
                        ].map(item => (
                            <ToggleButton
                                key={item.value}
                                value={item.value}
                                sx={{
                                    flex: 1, borderRadius: '12px !important',
                                    border: '1px solid #e2e8f0 !important',
                                    textTransform: 'none',
                                    gap: 0.5,
                                    py: 1.2,
                                    fontSize: '0.82rem',
                                    fontWeight: 600,
                                    '&.Mui-selected': {
                                        background: '#eff6ff',
                                        borderColor: '#3b82f6 !important',
                                        color: '#2563eb',
                                    },
                                }}
                            >
                                {item.icon} {item.label}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>

                    {/* 기기명 */}
                    <Typography fontWeight={600} sx={{ fontSize: '0.88rem', color: '#0f172a', mb: 1 }}>
                        매입 기기
                    </Typography>
                    <TextField
                        fullWidth
                        placeholder="예) 아이폰 15 Pro Max 256GB"
                        value={device}
                        onChange={e => setDevice(e.target.value)}
                        sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        size="small"
                    />

                    {/* 별점 */}
                    <Typography fontWeight={600} sx={{ fontSize: '0.88rem', color: '#0f172a', mb: 1 }}>
                        만족도
                    </Typography>
                    <Box sx={{ mb: 3 }}>
                        <Rating
                            value={rating}
                            onChange={(_, v) => setRating(v)}
                            size="large"
                            sx={{ '& .MuiRating-iconFilled': { color: '#f59e0b' } }}
                        />
                    </Box>

                    {/* 리뷰 내용 */}
                    <Typography fontWeight={600} sx={{ fontSize: '0.88rem', color: '#0f172a', mb: 1 }}>
                        리뷰 내용
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={5}
                        placeholder="거래 경험을 자유롭게 작성해주세요 (최소 20자)"
                        value={text}
                        onChange={e => setText(e.target.value)}
                        sx={{ mb: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', mb: 4, textAlign: 'right' }}>
                        {text.length}자
                    </Typography>

                    {/* 제출 */}
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={loading}
                        sx={{
                            py: 1.5,
                            borderRadius: 2.5,
                            fontWeight: 700,
                            fontSize: '0.95rem',
                            background: 'linear-gradient(135deg, #1e40af, #3b82f6)',
                            textTransform: 'none',
                            boxShadow: '0 4px 16px rgba(59,130,246,0.25)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #1d4ed8, #2563eb)',
                            },
                        }}
                    >
                        {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : "리뷰 등록하기"}
                    </Button>

                    <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', mt: 2, textAlign: 'center', lineHeight: 1.6 }}>
                        등록된 리뷰는 관리자 검토 후 메인 페이지에 게시됩니다.<br />
                        부적절한 내용은 게시가 거부될 수 있습니다.
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default ReviewWritePage;
